import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { format } from 'date-fns';
import {
  ChevronDownIcon,
  InfoIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import React, { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/Loader';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import {
  useDeleteMarkdownFile,
  useGetMarkdownFile,
  useUploadMarkdownFile,
} from '@/hooks/useMarkdownFile';
import { useMy } from '@/hooks/useMy';
import { MyFile } from '@/types';

interface MarkdownSectionLabels {
  title: string;
  tooltip: string;
  editingStatus: string;
  emptyTitle: string;
  emptyDescription: string;
  deleteModalTitle: string;
  deleteModalSubtitle: string;
}

interface VariantConfig {
  filePrefix: string;
}

export interface MarkdownSectionProps {
  customerEmail: string;
  isPraetorianUser: boolean;
  title?: string;
  showTitle?: boolean;
  className?: string;
}

export function createMarkdownSection(
  defaultFileName: string,
  defaultLabels: MarkdownSectionLabels,
  variantConfig?: VariantConfig
) {
  const getVariantLabel = (fileName: string): string => {
    // Strip reports/ prefix and .md extension for display
    let label = fileName;
    if (label.startsWith('reports/')) {
      label = label.slice('reports/'.length);
    }
    if (label.endsWith('.md')) {
      label = label.slice(0, -3);
    }
    return label;
  };

  const Section: React.FC<MarkdownSectionProps> = ({
    customerEmail,
    isPraetorianUser,
    title = defaultLabels.title,
    showTitle = true,
    className = '',
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [selectedVariant, setSelectedVariant] = useState<string | null>(null);

    const effectiveFileName = selectedVariant ?? defaultFileName;

    const { data, isPending } = useGetMarkdownFile(
      effectiveFileName,
      customerEmail
    );
    const { mutate: upload, isPending: isUploading } = useUploadMarkdownFile(
      effectiveFileName,
      customerEmail
    );
    const { mutate: remove, isPending: isDeleting } = useDeleteMarkdownFile(
      effectiveFileName,
      customerEmail
    );

    // Fetch variant files when variantConfig is provided
    const { data: allFiles } = useMy(
      { resource: 'file', query: 'reports/' },
      {
        enabled: !!variantConfig,
        email: customerEmail,
      }
    );

    // Filter and sort variant files
    const variantFiles = React.useMemo(() => {
      if (!variantConfig || !allFiles) return [];

      const prefix = `reports/${variantConfig.filePrefix}`;
      return (allFiles as MyFile[])
        .filter(
          f => f.name && f.name.startsWith(prefix) && f.name.endsWith('.md')
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [variantConfig, allFiles]);

    const content = data?.content ?? '';
    const lastModified = data?.lastModified ?? null;

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        upload(file);
      }
      // Reset input so same file can be re-selected
      event.target.value = '';
    };

    const handleUploadClick = () => {
      fileInputRef.current?.click();
    };

    const handleDeleteConfirm = () => {
      remove(undefined, {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
        },
      });
    };

    const handleEditClick = () => {
      setEditContent(content);
      setIsEditing(true);
    };

    const handleSave = () => {
      upload(editContent, {
        onSuccess: () => {
          setIsEditing(false);
        },
      });
    };

    const handleCancel = () => {
      setEditContent('');
      setIsEditing(false);
    };

    const handleVariantSelect = (fileName: string | null) => {
      if (isEditing) {
        setEditContent('');
        setIsEditing(false);
      }
      setSelectedVariant(fileName);
    };

    const showDropdown = !!variantConfig && !isEditing;

    return (
      <div
        className={`border-chariot-stroke bg-chariot-background-primary rounded-lg border ${className}`}
      >
        <div className="p-6">
          {/* Section Header */}
          {showTitle && (
            <div className="mb-4 flex items-center gap-2">
              <h3 className="text-xl font-bold">{title}</h3>
              <Tooltip title={defaultLabels.tooltip}>
                <InfoIcon className="text-chariot-text-secondary size-4" />
              </Tooltip>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.txt"
            className="hidden"
            onChange={handleFileSelect}
          />

          {/* Content area */}
          <Loader isLoading={isPending}>
            {content || isEditing || variantFiles.length > 0 ? (
              <div>
                {/* Sub-header bar */}
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-chariot-text-secondary text-sm">
                    {isEditing
                      ? defaultLabels.editingStatus
                      : `Last updated: ${lastModified ? format(lastModified, 'MMM d, yyyy h:mm a') : 'Unknown'}`}
                  </p>
                  <div className="flex items-center gap-2">
                    {/* Variant dropdown */}
                    {showDropdown && (
                      <Menu as="div" className="relative">
                        <MenuButton className="border-chariot-stroke text-chariot-text-primary hover:bg-chariot-background-secondary inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors">
                          {selectedVariant
                            ? getVariantLabel(selectedVariant)
                            : 'Current'}
                          <ChevronDownIcon className="size-4" />
                        </MenuButton>
                        <MenuItems
                          anchor="bottom end"
                          className="bg-chariot-background-primary border-chariot-stroke z-50 mt-1 w-48 rounded-md border shadow-lg"
                        >
                          <div className="py-1">
                            <MenuItem>
                              {({ focus }) => (
                                <button
                                  onClick={() => handleVariantSelect(null)}
                                  className={`${
                                    focus
                                      ? 'bg-chariot-background-secondary'
                                      : ''
                                  } text-chariot-text-primary w-full px-4 py-2 text-left text-sm ${
                                    selectedVariant === null ? 'font-bold' : ''
                                  }`}
                                >
                                  Current
                                </button>
                              )}
                            </MenuItem>
                            {variantFiles.map(file => (
                              <MenuItem key={file.name}>
                                {({ focus }) => (
                                  <button
                                    onClick={() =>
                                      handleVariantSelect(file.name)
                                    }
                                    className={`${
                                      focus
                                        ? 'bg-chariot-background-secondary'
                                        : ''
                                    } text-chariot-text-primary w-full px-4 py-2 text-left text-sm ${
                                      selectedVariant === file.name
                                        ? 'font-bold'
                                        : ''
                                    }`}
                                  >
                                    {getVariantLabel(file.name)}
                                  </button>
                                )}
                              </MenuItem>
                            ))}
                          </div>
                        </MenuItems>
                      </Menu>
                    )}

                    {isPraetorianUser && (
                      <>
                        {isEditing ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancel}
                              disabled={isUploading}
                            >Cancel</Button>
                            <Button
                              size="sm"
                              onClick={handleSave}
                              disabled={isUploading}
                            >{isUploading ? 'Saving...' : 'Save'}</Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleEditClick}
                            ><PencilIcon className="size-4" />Edit</Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleUploadClick}
                              disabled={isUploading}
                            ><UploadIcon className="size-4" />{isUploading ? 'Uploading...' : 'Upload'}</Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setIsDeleteModalOpen(true)}
                              disabled={isDeleting}
                            ><Trash2Icon className="size-4" />{isDeleting ? 'Deleting...' : 'Delete'}</Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Markdown content or editor */}
                {isEditing ? (
                  <div className="h-[500px]">
                    <MarkdownEditor
                      value={editContent}
                      onChange={value => setEditContent(value || '')}
                      email={customerEmail}
                      filePathPrefix="images"
                    />
                  </div>
                ) : (
                  <div className="border-chariot-stroke rounded-md border p-4">
                    <MarkdownPreview source={content} email={customerEmail} />
                  </div>
                )}
              </div>
            ) : (
              /* Empty state */
              <div className="border-chariot-stroke rounded-md border p-8 text-center">
                <p className="text-chariot-text-primary font-medium">
                  {defaultLabels.emptyTitle}
                </p>
                <p className="text-chariot-text-secondary mt-1 text-sm">
                  {defaultLabels.emptyDescription}
                </p>
                {isPraetorianUser && (
                  <div className="mt-4 flex justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditClick}
                    ><PlusIcon className="size-4" />Create</Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleUploadClick}
                      disabled={isUploading}
                    ><UploadIcon className="size-4" />{isUploading ? 'Uploading...' : 'Upload'}</Button>
                  </div>
                )}
              </div>
            )}
          </Loader>
        </div>

        {/* Delete confirmation modal */}
        <Modal
          title={defaultLabels.deleteModalTitle}
          subtitle={defaultLabels.deleteModalSubtitle}
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          footer={{
            text: isDeleting ? 'Deleting...' : 'Delete',
            onClick: handleDeleteConfirm,
            disabled: isDeleting,
          }}
        />
      </div>
    );
  };

  return Section;
}
